import { useQuery } from "@tanstack/react-query";

import { API_URL } from "@/constants/envVariables";

import { fetchApi } from "@/helpers/fetchApi";

import type { ApiOrganizationMultisig, AppError } from "@/types";

/**
 * The organization itself is loaded into Redux by `getOrgInfoAction`, which
 * keeps only the fields it maps, so the multisig fields get their own query.
 * Scoped under `"organization"` so invalidating that prefix refreshes it too.
 *
 * `GetOrganizationInfo` (`internal/serve/httphandler/profile_handler.go`)
 * serializes `multisig_status`, `multisig_governance_mode` and
 * `multisig_active_signer_count` — it does not carry signer identities.
 * Active signers' names/roles/keys come from each user's `multisig_signer`
 * field on `GET /users` (`useUsers`) instead.
 */
export const ORGANIZATION_MULTISIG_QUERY_KEY = ["organization", "multisig"] as const;

/** `GET /organization`, narrowed to its multisig fields. */
export const useOrganizationMultisig = (options?: { enabled?: boolean }) =>
  useQuery<ApiOrganizationMultisig | undefined, AppError, ApiOrganizationMultisig>({
    queryKey: ORGANIZATION_MULTISIG_QUERY_KEY,
    queryFn: async () => await fetchApi(`${API_URL}/organization`),
    select: (org) => ({
      multisig_status: org?.multisig_status,
      multisig_governance_mode: org?.multisig_governance_mode,
      multisig_active_signer_count: org?.multisig_active_signer_count,
    }),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
  });
